import { number, route, string, union } from 'react-router-typesafe-routes';
import { z } from 'zod';

export const handleSchema = z.object({
  showFooter: z.boolean().default(false),
});

export type RouteHandle = z.infer<typeof handleSchema>;

const collectionHashOptions = union(['all', 'media', 'text', 'files', 'audio']);
const mediaHashOptions = union(['all', 'images', 'videos', 'files']);

const ROUTES = {
  ONBOARD: route({
    path: 'onboard',
    searchParams: {
      locale: string(),
      brand: string(),
      app: string(),
      token: string(),
      screen: string(),
      redirect: string(),
      layout: string(),
      alwaysEnterAsSubmit: string(),
    },
  }),
  SEARCH: route({
    path: 'search',
    searchParams: {
      keyword: string(),
    },
    hash: union(['friends', 'groups', 'messages', 'all']),
    children: {
      BY_SESSION: route({
        path: ':session',
        params: {
          session: string(), //TODO: check if it's valid to remove
        },
      }),
    },
  }),
  AUTH: route({
    path: 'auth',
    children: {
      SIGN_IN: route({
        path: 'sign-in',
        searchParams: { phone: string(), redirect: string() },
        state: { from: union(['switch']) },
      }),
      SIGN_UP: route({ path: 'sign-up', searchParams: { phone: string() } }),
      SETUP: route({ path: 'setup' }),
      FORGOT_PASSWORD: route({ path: 'forgot-password' }),
      PRIVACY: route({ path: 'privacy' }),
      AGREEMENT: route({ path: 'agreement' }),
      SWITCH: route({ path: 'switch' }),
    },
  }),
  USER_BY_ID: route({
    path: 'users/:id',
    params: { id: number().defined() },
    searchParams: {
      from: union(['onboard', 'comm']),
    },
  }),
  GROUP_BY_ID: route({
    path: 'groups/:id',
    params: { id: number().defined() },
    searchParams: { from: union(['onboard', 'comm']), remark: string() },
  }),
  CONTACT: route({
    path: 'contact',
    children: {
      GROUPS: route({
        path: 'groups',
        children: {
          CREATE: route({
            path: 'create',
          }),
        },
      }),
      USERS: route({
        path: 'users',
        children: {
          APPLICATIONS: route({ path: 'applications', searchParams: { from: union(['onboard']) } }),
          BY_ID: route({
            path: ':userId',
            params: { userId: number().defined() },
            searchParams: {
              source: union(['contact', 'search', 'avatar', 'qrcode']),
              groupId: number(),
            },
            state: {
              from: union(['contact', 'session']),
            },
            children: {
              REMARK: route({ path: 'remark' }),
              GROUPS: route({ path: 'groups' }),
              TAGS: route({ path: 'tags' }),
            },
          }),
        },
      }),
      REQUEST: route({
        path: 'request',
        children: {
          USER: route({
            path: 'user',
            searchParams: {
              id: number(),
              code: string(),
              key: string(),
              source: union(['contact', 'search', 'avatar', 'qrcode']),
              sessionId: number(),
              groupId: number(),
            },
            state: {
              from: union(['comm']),
            },
          }),
          GROUP: route({
            path: 'group',
            searchParams: {
              code: string(),
            },
          }),
          INVITE: route({
            path: 'invite',
            searchParams: {
              code: string(),
            },
          }),
          SCAN: route({
            path: 'scan',
            children: {
              SHARE: route({ path: 'share', searchParams: { code: string() } }),
            },
          }),
        },
      }),
      TAGS: route({
        path: 'tags',
        children: {
          BY_ID: route({
            path: ':tagId',
            params: { tagId: number().defined() },
            children: { ADD_MEMBER: route({ path: 'add-member' }) },
          }),
        },
      }),
    },
  }),
  MY: route({
    path: 'my',
    state: { from: union(['comm']) },
    children: {
      INFO: route({ path: 'info' }),
      DEVELOPER: route({
        path: 'developer',
        children: {
          MESSAGING: route({ path: 'messaging' }),
          QUERIES: route({ path: 'queries' }),
        },
      }),
      QR_CODE: route({
        path: 'qrcode',
        searchParams: { from: union(['onboard']) },
        children: {
          SHARE: route({ path: 'share', searchParams: { code: string() } }),
        },
      }),
      NICKNAME: route({ path: 'nickname' }),
      SIGNATURE: route({ path: 'signature' }),
      BLACKLIST: route({ path: 'blacklist' }),
      STICKERS: route({ path: 'stickers' }),
      COLLECTIONS: route({
        path: 'collections',
        hash: collectionHashOptions,
        searchParams: { from: union(['onboard']) },
        state: { from: union(['comm']) },
        children: {
          BY_ID: route({
            path: ':collectionId',
            params: { collectionId: number().defined() },
            children: {
              FORWARD: route({ path: 'forward' }),
            },
            state: { skipPreview: union([1, 0]).default(0) },
          }),
        },
      }),
      SETTINGS: route({
        path: 'settings',
        children: {
          PRIVACY: route({ path: 'privacy', searchParams: { from: union(['onboard']) } }),
          MESSAGING: route({
            path: 'messaging',
            searchParams: { from: union(['onboard']) },
            children: {
              BATCH: route({
                path: 'batch',
                children: {
                  PREVIEW: route({
                    path: 'preview/:batchId',
                    params: { batchId: number().defined() },
                    children: {
                      FORWARD: route({
                        path: 'forward',
                      }),
                    },
                  }),
                  CREATE: route({
                    path: 'create',
                    children: {
                      BY_ID: route({ path: ':batchId', params: { batchId: number() } }),
                    },
                  }),
                },
              }),
            },
          }),
          NOTIFICATIONS: route({ path: 'notifications' }),
          SECURITY: route({
            path: 'security',
            children: {
              PASSWORD: route({ path: 'password' }),
            },
          }),
          GENERAL: route({
            path: 'general',
            children: {
              ABOUT_US: route({ path: 'about-us' }),
              FEEDBACK: route({ path: 'feedback' }),
              DEBUG: route({ path: 'debug' }),
              LOGS: route({ path: 'logs' }),
            },
          }),
        },
      }),
    },
  }),
  MESSAGES: route({
    path: 'messages',
    searchParams: { filter: union(['all', 'unread', 'groups']), from: union(['onboard']) },
    state: {
      from: union(['comm']),
    },
    children: {
      SESSIONS: route({
        path: 'sessions/:id',
        params: { id: number().defined() },
        searchParams: { search: number(), fromUnread: number() },
        state: {
          from: union(['search', 'comm']),
        },
        children: {
          SETTINGS: route({
            path: 'settings',
            state: {
              from: union(['comm']),
            },
          }),
          FORWARD: route({
            path: 'forward',
            searchParams: {
              messageIds: number().array(),
            },
          }),
          SEARCH: route({ path: 'search', searchParams: { keyword: string() } }),
          CONTACT: route({ path: 'contact' }),
          COLLECTIONS: route({
            path: 'collections',
            hash: collectionHashOptions,
            children: {
              BY_ID: route({
                path: ':collectionId',
                params: { collectionId: number().default(0) },
              }),
            },
          }),
          MEDIA: route({
            path: 'media',
            hash: mediaHashOptions,
            children: {
              BY_ID: route({
                path: ':messageId',
                params: { messageId: number().defined() },
                searchParams: { filterTypes: mediaHashOptions.array() },
                state: { from: union(['message', 'index']) },
              }),
            },
          }),
          MESSAGE_BY_ID: route({
            path: ':messageId',
            params: { messageId: number().defined() },
          }),
          REPORT: route({
            path: 'report',
            searchParams: {
              reasonId: number(),
            },
          }),
        },
      }),
      GROUPS: route({
        path: 'groups/:id',
        params: { id: number().defined() },
        searchParams: { search: number(), fromUnread: number() },
        state: {
          from: union(['search', 'comm']),
        },
        children: {
          SETTINGS: route({
            path: 'settings',
            state: {
              from: union(['comm']),
            },
            children: {
              NICKNAME: route({ path: 'nickname' }),
              ANNOUNCEMENT: route({ path: 'announcement' }),
              QR_CODE: route({
                path: 'qrcode',
                children: {
                  SHARE: route({ path: 'share', searchParams: { code: string() } }),
                },
              }),
              MEMBERS: route({
                path: 'members',
                children: {
                  INVITE: route({ path: 'invite' }),
                },
              }),
              ADMIN: route({
                path: 'admin',
                children: {
                  APPLICATIONS: route({ path: 'applications' }),
                  INFO: route({ path: 'info' }),
                  ADMINS: route({
                    path: 'admins',
                    children: {
                      ADD: route({ path: 'add' }),
                    },
                  }),
                },
              }),
            },
          }),
          MESSAGE_BY_ID: route({
            path: ':messageId',
            params: { messageId: number().defined() },
          }),
          FORWARD: route({
            path: 'forward',
            searchParams: {
              messageIds: number().array(),
            },
          }),
          SEARCH: route({ path: 'search', searchParams: { keyword: string() } }),
          CONTACT: route({ path: 'contact' }),
          COLLECTIONS: route({
            path: 'collections',
            hash: collectionHashOptions,
            children: {
              BY_ID: route({
                path: ':collectionId',
                params: { collectionId: number().default(0) },
              }),
            },
          }),
          MEDIA: route({
            path: 'media',
            hash: mediaHashOptions,
            children: {
              BY_ID: route({
                path: ':messageId',
                params: { messageId: number().defined() },
                searchParams: { filterTypes: mediaHashOptions.array() },
                state: { from: union(['message', 'index']) },
              }),
            },
          }),
          REPORT: route({
            path: 'report',
            searchParams: {
              reasonId: number(),
            },
          }),
        },
      }),
    },
  }),
};

export default ROUTES;

export type PromiseLoaderReturnType = Nullable<{ promises: Promise<void> }>;
